import React from "react";
import { useQuery } from "@tanstack/react-query";
import { QUERY_KEYS } from "library/shared";
import api from "service/api";
import useNotificationHandler from "hooks/useNotificationHandler";

// Client search result interface based on GOS API response
export interface ClientSearchResult {
  partnerID: string;
  partnerName: string;
  partnerNumber: string;
  firstName?: string;
  lastName?: string;
  isCompany: boolean;
  isPrivate: boolean;
  isInsurer: boolean;
  isIntermediary: boolean;
  isResident: boolean;
  companyRegisterNumber?: string;
  personIdentificationNumber?: string;
  partnerMainSicCode?: number;
  partnerAddress?: {
    house?: string;
    street?: string;
    city?: string;
    zipCode?: string;
    countryCodeID?: number;
    buildingFloorDoor?: string;
  };
  partnerPhone?: {
    phoneTypeCodeID?: number;
    phoneCountry?: string;
    phoneArea?: string;
    phoneExtension?: string;
    phoneNumber?: string;
  };
  partnerMail?: {
    emailAddress?: string;
    emailTypeCodeID?: number;
  };
  partnerFax?: {
    faxTypeCodeID?: number;
    faxCountry?: string;
    faxArea?: string;
    faxExtension?: string;
    faxNumber?: string;
  };
}

/**
 * Search for clients by text query
 * @param searchText - Search query string
 * @returns Array of matching clients
 */
const searchClients = async (
  searchText: string
): Promise<ClientSearchResult[]> => {
  if (!searchText || searchText.trim().length < 2) {
    return [];
  }

  const response = await api.get(
    `/clientssearch/${encodeURIComponent(searchText)}`
  );
  return response.data;
};

/**
 * Hook for searching clients
 * @param searchText - Search query string
 * @param options - Query options
 */
export function useClientSearch(
  searchText: string,
  options?: {
    enabled?: boolean;
    onSuccess?: (data: ClientSearchResult[]) => void;
    onError?: (error: Error) => void;
  }
) {
  const { handleAddAxiosErrorNotification } = useNotificationHandler();

  const query = useQuery<ClientSearchResult[], Error>({
    queryKey: [QUERY_KEYS.clientSearch, searchText],
    queryFn: () => searchClients(searchText),
    enabled: options?.enabled ?? searchText?.trim().length >= 2,
    staleTime: 1000 * 60 * 5, // Cache results for 5 minutes
    refetchOnWindowFocus: false,
  });

  // Handle callbacks with useEffect in v5
  React.useEffect(() => {
    if (query.isSuccess && options?.onSuccess) {
      options.onSuccess(query.data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.isSuccess, query.data]);

  React.useEffect(() => {
    if (query.isError) {
      handleAddAxiosErrorNotification(query.error);
      if (options?.onError) {
        options.onError(query.error);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.isError, query.error]);

  return query;
}

/**
 * Debounced client search hook
 * Useful for search-as-you-type functionality
 */
export function useDebouncedClientSearch(
  searchText: string,
  debounceMs: number = 300,
  options?: {
    enabled?: boolean;
    onSuccess?: (data: ClientSearchResult[]) => void;
    onError?: (error: Error) => void;
  }
) {
  const [debouncedSearchText, setDebouncedSearchText] =
    React.useState(searchText);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [searchText, debounceMs]);

  return useClientSearch(debouncedSearchText, options);
}
